/**
 * Aspect — Parse a restyle prompt into local finish / theme actions
 * --------------------------------------------------------------
 * Lets the prompt bar change walls (and named style themes) without an LLM
 * round-trip. Used when the orchestrator is down, and as a fast path when
 * the user names a finish we already have in Finishes.
 */

const WALLS = [
  { id: "wall_wood_panels", re: /\b(walnut|wood panel|paneling|wainscot|raised panel)\b/ },
  { id: "wall_exposed_brick", re: /\b(brick|loft brick|exposed brick)\b/ },
  { id: "wall_river_stone", re: /\b(river stone|stacked stone|stone wall|stone)\b/ },
  { id: "wall_concrete", re: /\b(concrete)\b/ },
  { id: "wall_paint_sage", re: /\b(sage)\b/ },
  { id: "wall_paint_sand", re: /\b(sand|warm sand|beige)\b/ },
  { id: "wall_paint_slate", re: /\b(slate)\b/ },
  { id: "wall_paint_white", re: /\b(white paint|white wall)\b/ },
  { id: "wall_plaster", re: /\b(plaster)\b/ },
];

const FLOORS = [
  { id: "floor_herringbone", re: /\b(herringbone|parquet)\b/ },
  { id: "floor_dark_wood", re: /\b(dark wood|stained oak)\b/ },
  { id: "floor_marble", re: /\b(marble)\b/ },
  { id: "floor_tile_large", re: /\b(tile|porcelain)\b/ },
  { id: "floor_laminate", re: /\b(laminate|oak floor)\b/ },
];

const THEMES = [
  { id: "scandinavian", re: /\b(scandinavian|scandi|hygge)\b/ },
  { id: "modern", re: /\b(modern|industrial|minimal)\b/ },
  { id: "classic", re: /\b(classic|traditional|warm classic)\b/ },
];

function firstMatch(list, text) {
  for (const item of list) {
    if (item.re.test(text)) return item.id;
  }
  return null;
}

/**
 * @param {string} prompt
 * @returns {{ theme: string|null, finishes: { wall?: string, floor?: string, floorWet?: string }, finishOnly: boolean, matched: boolean }}
 */
export function parseRestyleIntent(prompt) {
  const text = String(prompt ?? "").toLowerCase();
  const theme = firstMatch(THEMES, text);
  const finishes = {};
  const wall = firstMatch(WALLS, text);
  const floor = firstMatch(FLOORS, text);
  if (wall) finishes.wall = wall;
  if (floor) {
    finishes.floor = floor;
    if (floor === "floor_marble" || floor === "floor_tile_large") finishes.floorWet = floor;
  }

  const wantsFurniture = /\b(furnish|furniture|sofa|couch|restage|redo|replace|redecorate)\b/.test(text);
  const finishOnly = !theme && Object.keys(finishes).length > 0 && !wantsFurniture;
  return {
    theme,
    finishes,
    finishOnly,
    matched: Boolean(theme || Object.keys(finishes).length),
  };
}

export function orchUserError(err, status = 0) {
  const prod = Boolean(import.meta.env?.PROD);
  if (status === 401) return "Session expired — refresh the page and sign in again.";
  if (status === 403) return "That restyle stays in your tour. Try Send again, or pick Finishes.";
  if (status === 502 || status === 503) return "The tour server is restarting — wait a few seconds and Send again.";
  const msg = String(err?.message ?? err ?? "");
  if (prod && /Failed to fetch|NetworkError|orchestrator|network/i.test(msg)) {
    return "Couldn't reach the restyle service. Check your connection and try Send again.";
  }
  if (/Failed to fetch|NetworkError|orchestrator|network/i.test(msg)) {
    return "Can't reach the orchestrator. Start it with `npm run orchestrator`, or pick Demo (no key) in the model menu.";
  }
  return msg || "Restyle failed.";
}

export default parseRestyleIntent;
